// Shared assessment-score normalisation — the SINGLE source of truth for
// turning the /workflow/assessment API payload into the three 0-100 scores
// AssessmentCards renders.
//
// Why this exists (Demo UX findings, 22 Aug 2026): ProjectView and
// OrchestratorTab each had their OWN parser for this payload, and they
// disagreed. The live backend (lambda/innovateflow-api/workflow/assessment.py)
// stores axes keyed `novelty` / `feasibility` / `market_potential`, each
// `{score: 0-10}`. ProjectView's parser looked for `market`/`technical` keys
// that never occur in that shape, so it returned undefined — the Overview tab
// showed "Not assessed" ×3 while the Workflow Progress stepper (driven by a
// looser "axes object has keys" check) marked Assessment complete, and the
// Next Steps tab (whose own parser handled the axes shape) showed real
// numbers. Three views, three parsers, two contradicting stories on one page.
//
// Every consumer now derives BOTH "do scores exist?" and "what are they?"
// from this one function, so the stepper, the Overview panel and the Next
// Steps tab can never contradict each other again.

import type { AssessmentApiResponse } from '../../types'

export interface AssessmentScores {
  ip_novelty: number
  market_feasibility: number
  technical_feasibility: number
}

// The two payload shapes the API can return (see AssessmentApiResponse in
// types/index.ts):
//   { assessment: { axes: { novelty: {score}, feasibility: {score}, market_potential: {score} } } }
//     ← the live backend; scores are 0-10 and are scaled ×10 for /100 display
//   { scores: { ip_novelty, market_feasibility, technical_feasibility } }
//     ← legacy flat shape (WorkflowInstance.assessment_scores); already 0-100

const num = (v: unknown): number | undefined =>
  typeof v === 'number'
    ? v
    : typeof (v as { score?: unknown } | null | undefined)?.score === 'number'
      ? (v as { score: number }).score
      : undefined

// Three-state assessment read (Story #6172 Task #6216). Demo UX findings
// (22 Aug 2026) fixed "no assessment" vs "real scores" disagreeing across
// views; F11 (Task #6213) added a THIRD real state — the engine ran and
// deliberately recorded `recommendation: 'insufficient_basis'` because the
// project had no real content to score honestly. That state's axes carry
// `score: null`, so normalizeAssessmentScores() already returns undefined
// for it — collapsing it into the same bucket as "no assessment exists at
// all" would be exactly the class of bug the 22 Aug incident was: every
// consumer must be able to tell "hasn't run yet" apart from "ran, and
// honestly found nothing to score" apart from "ran, here are real numbers".
export type AssessmentState =
  | { kind: 'none' }
  | { kind: 'insufficient_basis' }
  | { kind: 'scored'; scores: AssessmentScores }

// Single wording for the insufficient-basis explanation, shared by every
// consumer that renders ConfidenceGate for this state — so the Overview
// panel and the Orchestrator tab can never say two different things about
// the same project (the exact failure class the 22 Aug incident was).
export const INSUFFICIENT_BASIS_REASON =
  "There isn't enough real content in this invention's documents yet to score honestly. Add an invention declaration or documents with real detail — claims, a description — then run the assessment again."

export function deriveAssessmentState(data: unknown): AssessmentState {
  const d = data as AssessmentApiResponse | null | undefined
  if (d?.assessment?.recommendation === 'insufficient_basis') {
    return { kind: 'insufficient_basis' }
  }
  const scores = normalizeAssessmentScores(data)
  return scores ? { kind: 'scored', scores } : { kind: 'none' }
}

export function normalizeAssessmentScores(data: unknown): AssessmentScores | undefined {
  const d = data as AssessmentApiResponse | null | undefined
  if (!d) return undefined

  // Legacy flat shape — values are already on the 0-100 display scale.
  const s = d.scores
  if (s) {
    const ip = num(s.ip_novelty ?? s.invention_novelty ?? s.novelty)
    const market = num(s.market_feasibility ?? s.market ?? s.market_potential)
    const tech = num(s.technical_feasibility ?? s.technical ?? s.feasibility)
    if (ip != null && market != null && tech != null) {
      return { ip_novelty: ip, market_feasibility: market, technical_feasibility: tech }
    }
  }

  // Live backend axes shape — 0-10 per axis, scaled ×10 (and clamped) so a
  // stored 7.2 renders as 72/100, matching what the assessment engine meant.
  const axes = d.assessment?.axes
  if (axes) {
    const ip = num(axes.novelty ?? axes.ip_novelty ?? axes.invention_novelty)
    const market = num(axes.market_potential ?? axes.market_feasibility ?? axes.market)
    const tech = num(axes.feasibility ?? axes.technical_feasibility ?? axes.technical)
    if (ip != null && market != null && tech != null) {
      const scale = (v: number) => Math.round(Math.max(0, Math.min(10, v)) * 10)
      return { ip_novelty: scale(ip), market_feasibility: scale(market), technical_feasibility: scale(tech) }
    }
  }

  return undefined
}
